import React from 'react';
import { Alert, Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { QcCategoriesState, sectionsOf } from './qcCategories';
import { READABLE_CHIP_SX } from './qcTokens';


/** Categories picked plus a note. The shape both anchors share; an image adds an outcome. */
export interface CategoryFeedbackValue {
    /** `SurveyField` ids of the categories picked. */
    categoryIds: number[];
    comment: string;
}

interface CategoryFeedbackProps {
    categories: QcCategoriesState;
    value: CategoryFeedbackValue;
    onChange: (next: CategoryFeedbackValue) => void;
    readOnly?: boolean;
    /**
     * Render each section's name above its chips.
     *
     * True for an image, which receives two sections — the universal one and the one for
     * its shot type — where the heading is the only thing telling them apart. False for a
     * lesion, which receives one section inside a panel that already names itself, so the
     * heading would just repeat it.
     */
    showSectionHeadings?: boolean;
    /** What read-only says when nothing was recorded. Omitted renders nothing. */
    emptyReadOnlyMessageId?: string;
    /**
     * Placeholder for the note field, as a translation id.
     *
     * It carries who the note is addressed to, which is the one thing an empty textarea
     * cannot convey and the reviewer has no other way to learn: feedback here is read by
     * the dermatologist who collected the images, not by another reviewer. The two anchors
     * name different recipients, so the copy is the caller's rather than a constant here.
     */
    notePlaceholderId?: string;
    /**
     * Whether the note field is offered at all. Defaults to true. False for flagging a visit
     * without the "Other" reason (SKIN-577), where the reasons say everything and a note
     * nobody asked for is free text that must not identify the patient.
     */
    showNote?: boolean;
}

/**
 * SKIN-241 — pick categories, write a note. Shared by the per-image assessment and the
 * per-lesion panel.
 *
 * Extracted after review found the two had been written twice and had already drifted in
 * three places: the category-fetch failure rendered as an `Alert` on one and a red caption
 * on the other, only one of them had a "nothing was said" read-only message, and only one
 * labelled its sections. Two copies of a widget do not stay identical; they diverge
 * silently and each divergence looks deliberate to the next reader.
 *
 * What is genuinely different between the two anchors stays outside this component: an
 * image has an outcome that gates whether feedback exists at all, and a lesion has a
 * heading naming which shot set it governs. Those live in their own components.
 *
 * Categories are per-study configuration and their labels are study content, so nothing
 * here is translated except the chrome.
 */
export default function CategoryFeedback({
    categories,
    value,
    onChange,
    readOnly = false,
    showSectionHeadings = false,
    emptyReadOnlyMessageId,
    notePlaceholderId,
    showNote = true,
}: CategoryFeedbackProps) {
    const sections = sectionsOf(categories);

    const labelById = new Map<number, string>();
    for (const section of sections) {
        for (const category of section.fields) labelById.set(category.id, category.label);
    }

    if (readOnly) {
        const hasFeedback = value.categoryIds.length > 0 || Boolean(value.comment);
        if (!hasFeedback) {
            return emptyReadOnlyMessageId ? (
                <Typography variant="body2" color="text.secondary" data-testid="feedback-empty">
                    <Translate id={emptyReadOnlyMessageId} />
                </Typography>
            ) : null;
        }
        return (
            <Box data-testid="feedback-static">
                {value.categoryIds.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {value.categoryIds.map((id) => (
                            <Chip
                                key={id}
                                size="small"
                                variant="outlined"
                                data-testid="category-static"
                                data-category-id={id}
                                sx={READABLE_CHIP_SX}
                                // A category whose configuration has since been deactivated
                                // still renders: the feedback was given about a real
                                // photograph, and the reviewer's record of it does not
                                // expire because a study admin later edited the form.
                                label={labelById.get(id) ?? `#${id}`}
                            />
                        ))}
                    </Box>
                )}
                {value.comment && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} data-testid="comment-static">
                        {value.comment}
                    </Typography>
                )}
            </Box>
        );
    }

    const toggleCategory = (id: number) => {
        const picked = value.categoryIds.includes(id);
        onChange({
            ...value,
            categoryIds: picked
                ? value.categoryIds.filter((c) => c !== id)
                : [...value.categoryIds, id],
        });
    };

    return (
        <Box data-testid="feedback-controls">
            {categories.status === 'loading' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="categories-loading">
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                        <Translate id="hospital.quality-check.review.categories-loading" />
                    </Typography>
                </Box>
            )}

            {/*
              * A failed category fetch must not block the review. The outcome is what gates
              * approval and the note is free text, so losing the list costs the reviewer
              * structure, not the ability to report a problem.
              */}
            {categories.status === 'failed' && (
                <Alert severity="warning" data-testid="categories-error" sx={{ py: 0 }}>
                    {categories.error}
                </Alert>
            )}

            {sections.map((section) => (
                <Box key={section.name} sx={{ mt: 1 }} data-testid="category-section" data-section={section.name}>
                    {showSectionHeadings && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                            {/* Study content, not UI chrome — rendered as the API sent it. */}
                            {section.name}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {section.fields.map((category) => {
                            const picked = value.categoryIds.includes(category.id);
                            return (
                                <Chip
                                    key={category.id}
                                    size="small"
                                    label={category.label}
                                    data-testid="category-chip"
                                    data-category-id={category.id}
                                    data-selected={picked}
                                    color={picked ? 'primary' : 'default'}
                                    variant={picked ? 'filled' : 'outlined'}
                                    onClick={() => toggleCategory(category.id)}
                                    sx={READABLE_CHIP_SX}
                                />
                            );
                        })}
                    </Box>
                </Box>
            ))}

            {/*
              * The placeholder is resolved through Translate's render prop rather than a
              * `<Translate id>` element, because a placeholder is a string attribute and an
              * element rendered into it stringifies to `[object Object]` — the same trap the
              * queue's native `<option>` labels hit.
              */}
            {showNote && (
                <Translate>
                    {({ translate }) => (
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            size="small"
                            sx={{ mt: 1 }}
                            value={value.comment}
                            placeholder={notePlaceholderId ? String(translate(notePlaceholderId)) : undefined}
                            onChange={(event) => onChange({ ...value, comment: event.target.value })}
                            inputProps={{ 'data-testid': 'feedback-comment' }}
                        />
                    )}
                </Translate>
            )}
        </Box>
    );
}
